'use client';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { MediaQuery } from '@s7/base';
import { Stack, Center } from '@s7/layout';
import { rem } from '@s7/styles';
import { Divider, Loader } from '@s7/ui';
import { SearchLayoutBlock } from './SearchLayoutBlock.js';
import { SearchLayoutDivider } from './SearchLayoutDivider.js';
import { SearchLayoutItem } from './SearchLayoutItem.js';
import { SearchLayoutRoot } from './SearchLayoutRoot.js';
import { countItems } from '../../utils/countItems.js';
import { NavSearchItem } from '../../slots/NavSearchItem/NavSearchItem.js';
import { HelpCenterItem } from '../../slots/HelpCenterItem/HelpCenterItem.js';
import { ShowMore } from '../../slots/ShowMore/ShowMore.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var showMoreSX = function(theme) {
    var _obj;
    return _obj = {
        marginLeft: rem(24)
    }, _obj[theme.fn.up("md1")] = {
        marginLeft: rem(40)
    }, _obj;
};
var SearchLayout = function(props) {
    var _navigation_items, _helpCenter_items, _navigation_items1, _helpCenter_items1, _navigation_items2, _helpCenter_items2;
    var highlight = props.highlight, loading = props.loading, hovered = props.hovered, onItemHover = props.onItemHover, navigation = props.navigation, helpCenter = props.helpCenter, Fallback = props.renderSearchFallback, showMore = props.showMore, _props_totalItems = props.totalItems, totalItems = _props_totalItems === void 0 ? countItems(navigation == null ? void 0 : navigation.items, helpCenter == null ? void 0 : helpCenter.items, showMore) : _props_totalItems, onNavigationItemClick = props.onNavigationItemClick, onItemClick = props.onItemClick, onShowMoreClick = props.onShowMoreClick, others = _object_without_properties_loose(props, [
        "highlight",
        "loading",
        "hovered",
        "onItemHover",
        "navigation",
        "helpCenter",
        "renderSearchFallback",
        "showMore",
        "totalItems",
        "onNavigationItemClick",
        "onItemClick",
        "onShowMoreClick"
    ]);
    var isCategoriesEmpty = !(navigation == null ? void 0 : (_navigation_items = navigation.items) == null ? void 0 : _navigation_items.length) && !(helpCenter == null ? void 0 : (_helpCenter_items = helpCenter.items) == null ? void 0 : _helpCenter_items.length);
    if (!loading && isCategoriesEmpty) {
        return Fallback ? /*#__PURE__*/ jsx(SearchLayoutRoot, _extends({}, others, {
            children: /*#__PURE__*/ jsx(Fallback, {})
        })) : null;
    }
    var showNavigation = !!(navigation == null ? void 0 : (_navigation_items1 = navigation.items) == null ? void 0 : _navigation_items1.length);
    var showHelpCenter = helpCenter == null ? void 0 : (_helpCenter_items1 = helpCenter.items) == null ? void 0 : _helpCenter_items1.length;
    var NavigationItemsLayout = showNavigation ? /*#__PURE__*/ jsx(SearchLayoutBlock, {
        title: navigation == null ? void 0 : navigation.title,
        children: /*#__PURE__*/ jsx(Stack, {
            divider: /*#__PURE__*/ jsx(SearchLayoutDivider, {}),
            spacing: 0,
            children: navigation == null ? void 0 : (_navigation_items2 = navigation.items) == null ? void 0 : _navigation_items2.map(function(item, index) {
                return /*#__PURE__*/ jsx(SearchLayoutItem, {
                    selected: index === hovered,
                    onMouseEnter: function() {
                        onItemHover(index);
                    },
                    children: /*#__PURE__*/ jsx(NavSearchItem, _extends({
                        "data-hovered": index === hovered,
                        highlight: highlight || navigation.highlight
                    }, item, {
                        onClick: function(event) {
                            onNavigationItemClick == null ? void 0 : onNavigationItemClick(item, event, index);
                        }
                    }))
                }, item.id);
            })
        })
    }) : null;
    var BlocksDivider = showNavigation ? /*#__PURE__*/ jsx(MediaQuery, {
        query: "md1.up",
        styles: {
            display: "none"
        },
        children: /*#__PURE__*/ jsx(Divider, {
            sx: {
                padding: rem("2 0")
            }
        })
    }) : null;
    var HelpCenterItemsList = !loading && showHelpCenter && helpCenter ? /*#__PURE__*/ jsx(SearchLayoutBlock, {
        title: helpCenter == null ? void 0 : helpCenter.title,
        children: helpCenter.items && /*#__PURE__*/ jsx(Stack, {
            divider: /*#__PURE__*/ jsx(SearchLayoutDivider, {}),
            spacing: 0,
            children: (_helpCenter_items2 = helpCenter.items) == null ? void 0 : _helpCenter_items2.map(function(item, index) {
                var isHovered = (navigation == null ? void 0 : navigation.items) ? navigation.items.length + index === hovered : index === hovered;
                var itemIndex = (navigation == null ? void 0 : navigation.items) ? navigation.items.length + index : index;
                return /*#__PURE__*/ jsx(SearchLayoutItem, {
                    selected: isHovered,
                    onMouseEnter: function() {
                        onItemHover(itemIndex);
                    },
                    children: /*#__PURE__*/ jsx(HelpCenterItem, _extends({
                        highlight: highlight || helpCenter.highlight
                    }, item, {
                        "data-hovered": isHovered,
                        onClick: function(event) {
                            onItemClick == null ? void 0 : onItemClick(item, event, itemIndex);
                        }
                    }))
                }, item.id);
            })
        })
    }) : null;
    var HelpCenterShowMoreAction = !loading && showHelpCenter && showMore ? /*#__PURE__*/ jsxs(Stack, {
        spacing: 16,
        children: [
            /*#__PURE__*/ jsx(SearchLayoutDivider, {}),
            /*#__PURE__*/ jsx(ShowMore, _extends({}, showMore, {
                "data-hovered": hovered ? hovered + 1 === totalItems : undefined,
                sx: showMoreSX,
                onClick: function(event) {
                    onShowMoreClick == null ? void 0 : onShowMoreClick(showMore, event, totalItems - 1);
                }
            }))
        ]
    }) : null;
    var HelpCenterItemsLayout = showHelpCenter ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            BlocksDivider,
            /*#__PURE__*/ jsxs(Stack, {
                spacing: 0,
                children: [
                    HelpCenterItemsList,
                    HelpCenterShowMoreAction
                ]
            })
        ]
    }) : null;
    return /*#__PURE__*/ jsxs(SearchLayoutRoot, _extends({}, others, {
        onMouseLeave: function() {
            return onItemHover(-1);
        },
        children: [
            /*#__PURE__*/ jsxs(Stack, {
                children: [
                    NavigationItemsLayout,
                    HelpCenterItemsLayout
                ]
            }),
            loading && /*#__PURE__*/ jsx(Center, {
                sx: {
                    height: rem(56)
                },
                children: /*#__PURE__*/ jsx(Loader, {
                    color: "primary.mid",
                    size: 36
                })
            })
        ]
    }));
};

export { SearchLayout };
